/**
 * Error returned by the API as RFC 9457 Problem Details.
 */
export class ApiError extends Error {
  /** HTTP status code. */
  readonly status: number;

  /** Stable error code (`TT-xxxx`), or `null` if the response carried none. */
  readonly code: string | null;

  /** Human-readable detail from the server, if any. */
  readonly detail: string | null;

  /**
   * Creates an API error.
   *
   * @param status - HTTP status code.
   * @param code - Stable error code or `null`.
   * @param detail - Detail text or `null`.
   */
  constructor(status: number, code: string | null, detail: string | null) {
    super(`API request failed with status ${String(status)}${code === null ? "" : ` (${code})`}.`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/**
 * The request did not reach the server or no response arrived (offline, DNS, CORS, timeout).
 */
export class NetworkError extends Error {
  /**
   * Creates a network error.
   *
   * @param cause - The underlying error.
   */
  constructor(cause: unknown) {
    super("The server could not be reached.", { cause });
    this.name = "NetworkError";
  }
}

/**
 * The response did not match the expected schema (contract violation between client and server).
 */
export class ResponseValidationError extends Error {
  /** The request path, starting with `/api/v1`. */
  readonly path: string;

  /**
   * Creates a validation error.
   *
   * @param path - The request path.
   * @param cause - The schema error.
   */
  constructor(path: string, cause: unknown) {
    super(`The response of '${path}' does not match the expected schema.`, { cause });
    this.name = "ResponseValidationError";
    this.path = path;
  }
}

/**
 * No access token is available; the user has to sign in.
 */
export class AuthenticationRequiredError extends Error {
  /** Creates the error. */
  constructor() {
    super("Authentication is required.");
    this.name = "AuthenticationRequiredError";
  }
}
